import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import {
    ApplicationInsights,
    DistributedTracingModes,
    IAutoExceptionTelemetry,
    ITelemetryItem,
    SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { TrackingConfig } from './tracking-config.model';
import { TRACKING_CONFIG } from './tracking-config.token';

@Injectable()
export class TrackingService {
    private config = inject<TrackingConfig>(TRACKING_CONFIG);

    private router = inject(Router);
    private readonly ai?: ApplicationInsights;
    private baseUrl = '';
    private assetsUrl = '';

    constructor() {
        if (this.config.aiConnectionString) {
            this.baseUrl = this.config.serverBaseUrl.slice(0, this.config.serverBaseUrl.length - 1);
            this.assetsUrl = this.config.serverBaseUrl + 'assets';
            const angularPlugin = new AngularPlugin();
            this.ai = new ApplicationInsights({
                config: {
                    appId: this.config.version + ';' + location.host,
                    autoTrackPageVisitTime: true,
                    connectionString: this.config.aiConnectionString,
                    correlationHeaderDomains: [new URL(this.config.serverBaseUrl).hostname],
                    disableDataLossAnalysis: false,
                    disableFetchTracking: true,
                    disableAjaxTracking: true,
                    distributedTracingMode: DistributedTracingModes.W3C,
                    enableAutoRouteTracking: true,
                    enableAjaxErrorStatusText: true,
                    enableAjaxPerfTracking: true,
                    enableCorsCorrelation: true,
                    enableUnhandledPromiseRejectionTracking: false, // if true produce a lot error in mapbox Safari 13 https://bugs.webkit.org/show_bug.cgi?id=215771
                    extensions: [angularPlugin],
                    extensionConfig: {
                        [angularPlugin.identifier]: { router: this.router },
                    },
                    maxAjaxCallsPerView: -1,
                },
            });
            this.ai.addTelemetryInitializer(this.enrichTelemetry);
            this.ai.addTelemetryInitializer(this.filterTelemetry);
            this.ai.loadAppInsights();
            this.ai.context.application.ver = this.config.version;
            this.ai.trackPageView();
        }
    }

    enrichTelemetry = (item: ITelemetryItem): void => {
        if (item.tags == null) {
            item.tags = [];
        }
        item.tags['ai.cloud.role'] = location.origin;
        item.tags['ai.cloud.roleInstance'] = this.config.version;

        if (item.data == null) {
            item.data = {};
        }

        item.data['isSupportedBrowser'] = this.config.isSupportedBrowser;

        if (this.config.mobileAppVersion) {
            item.data['mobileAppVersion'] = this.config.mobileAppVersion;
        }

        item.data['resolution'] = `${window.screen.width}x${window.screen.height}`;
        item.data['userAgent'] = navigator.userAgent;
        item.data['width'] = document.body.clientWidth.toString();
        item.data['height'] = document.body.clientHeight.toString();
    };
    filterTelemetry = (item: ITelemetryItem): boolean => {
        if (item.baseType != 'RemoteDependencyData') {
            return true;
        }

        if (item.baseData == null) {
            return true;
        }

        const target = item.baseData['target'];
        if (target == null || typeof target !== 'string') {
            return true;
        }

        return target.startsWith(this.baseUrl) && !target.startsWith(this.assetsUrl);
    };

    setUser(userId?: string, customerId?: string): void {
        if (userId === undefined) {
            this.ai?.clearAuthenticatedUserContext();
        } else {
            this.ai?.setAuthenticatedUserContext(userId, customerId, true);
        }
    }

    trackPageView(name?: string, url?: string): void {
        this.ai?.trackPageView({
            name: name,
            uri: url,
        });
    }

    trackEvent(name: string, properties?: { [key: string]: unknown }): void {
        this.ai?.trackEvent({ name: name }, properties);
    }

    trackMetric(name: string, average: number, properties?: { [key: string]: unknown }): void {
        this.ai?.trackMetric({ name: name, average: average }, properties);
    }

    trackException(exception: Error | IAutoExceptionTelemetry): void {
        console.error('EXCEPTION:', exception);
        this.ai?.trackException({ exception: exception, severityLevel: SeverityLevel.Error });
    }

    trackWarning(exception: Error | IAutoExceptionTelemetry): void {
        console.warn('Warning:', exception);
        this.ai?.trackException({ exception: exception, severityLevel: SeverityLevel.Warning });
    }

    trackTrace(message: string, properties?: { [key: string]: unknown }): void {
        this.ai?.trackTrace({ message: message, severityLevel: SeverityLevel.Information }, properties);
    }
}
